import {
  useGetUnitByPkQuery,
  UnitFragment,
  useUpdateUnitByPkMutation,
  useDeleteUnitByPkMutation,
} from '@app/generated/graphql';
import Page from '@app/components/Page';
import DescriptionIcon from '@material-ui/icons/Description';
import { pageContent } from '@app/shared/hoc/page-content';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PageTitle from '@app/shared/components/PageTitle';
import BackButton from '@app/shared/components/buttons/BackButton';
import { InfoCard } from '@mystiny/ui';
import { useResponseBar } from '@app/shared/hooks/useResponseBar';
import { FormProvider, useForm } from 'react-hook-form';
import Spacing from '@app/components/common/Spacing';
import SaveButton from '@app/shared/components/buttons/SaveButton';
import DeleteButton from '@app/shared/components/buttons/DeleteButton';
import { Form } from './components/Form';
import { SampleUnit } from './components/SampleUnit';
import { useHistory } from 'react-router-dom';

const UnitDetailForm = ({ data }: { data: UnitFragment }) => {
  const { id } = data;
  const history = useHistory();
  const form = useForm<UnitFragment>({
    defaultValues: data,
  });
  const { enqueueSuccess, enqueueFailure } = useResponseBar();
  const [updateFn, { loading: updating }] = useUpdateUnitByPkMutation({
    onCompleted: () => {
      enqueueSuccess('บันทึกข้อมูลสำเร็จ');
    },
    onError: (err: any) => {
      console.error(err);
      enqueueFailure('เกิดข้อผิดพลาด');
    },
  });

  const [deleteFn, { loading: deleting }] = useDeleteUnitByPkMutation({
    onCompleted: () => {
      enqueueSuccess('ลบข้อมูลสำเร็จ');
      history.push('/bof/projects/master-data/units');
    },
    onError: (err: any) => {
      console.error(err);
      enqueueFailure('เกิดข้อผิดพลาด');
    },
  });

  const handleSubmit = form.handleSubmit((formData: UnitFragment) => {
    updateFn({
      variables: {
        id: data.id,
        _set: {
          id: formData.id,
          description: formData.description,
        },
      },
    });
  });

  const handleDelete = useCallback(() => {
    if (
      window.confirm(
        'ท่านแน่ใจที่จะลบข้อมูลใช่หรือไม่?\n\nเป็นการลบข้อมูลออกจากระบบถาวร โดยที่จะไม่สามารถกู้คืนกลับมาได้'
      )
    ) {
      deleteFn({
        variables: {
          id,
        },
      });
    }
  }, [id, deleteFn]);

  const loading = updating || deleting;

  return (
    <Page
      title={
        <PageTitle
          title={
            <React.Fragment>
              <Typography variant="h6" component="div">
                <DescriptionIcon color="primary" fontSize="small" /> หน่วย (งบประมาณ-แตกตัวคูณ)
              </Typography>
              <Typography variant="h6" color="primary" component="div" gutterBottom>
                {data.description}
              </Typography>
            </React.Fragment>
          }
          actions={
            <div>
              <BackButton to="/bof/projects/master-data/units" />
            </div>
          }
        />
      }
    >
      <InfoCard
        title="ข้อมูลหน่วย"
        actions={
          <Grid container justify="space-between">
            <Grid item>
              <DeleteButton disabled={loading} onClick={handleDelete} />
            </Grid>
            <Grid item>
              <SaveButton disabled={loading} onClick={handleSubmit} />
            </Grid>
          </Grid>
        }
      >
        <FormProvider {...form}>
          <Form mode="update" />
        </FormProvider>
      </InfoCard>
      <Spacing />
      <InfoCard title="ทดสอบการแสดงผล">
        <SampleUnit data={data} />
      </InfoCard>
    </Page>
  );
};

const UnitDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading } = useGetUnitByPkQuery({
    variables: {
      id: id,
    },
  });

  if (loading && !data?.row) {
    return <CircularProgress />;
  }

  if (!data?.row) {
    return null;
  }

  return (
    <React.Fragment>
      <UnitDetailForm data={data?.row} />
    </React.Fragment>
  );
};

export default pageContent(UnitDetailPage);
